import { useState, useEffect, useCallback } from 'react'
import dayjs from 'dayjs'

import {
    fetchAllInvoices,
    updateInvoice,
    createInvoice,
    sendInvoiceEmail,
    getInvoiceById,
    getLastInvoiceNumber,
    fetchOrganizationName,
} from '../services/invoiceRecords.service'

import {
    fetchBillingRecords,
    updateBillingRecordWithInvoice,
    syncBillingRecordFromInvoice,
} from '../services/billingRecords.service'

import {
    exportToZoho,
    prepareZohoState,
} from '../pages/InvoiceRecords/utils/zohoHelpers'

/**
 * Unified Invoice + Billing Records Hook
 * Works for desktop & mobile
 */
const useInvoiceRecords = () => {
    /* ------------------------- State ------------------------- */

    const [invoices, setInvoices] = useState([])
    const [loading, setLoading] = useState(true)

    const [filters, setFilters] = useState({
        plantName: '',
        organization: '',
        status: '',
        period: null,
    })

    const [selectedInvoices, setSelectedInvoices] = useState([])
    const [page, setPage] = useState(0)
    const [rowsPerPage, setRowsPerPage] = useState(10)
    const [total, setTotal] = useState(0)

    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success',
    })

    const [isExporting, setIsExporting] = useState(false)

    /* --------------------- Snackbar --------------------- */

    const showSnackbar = (message, severity = 'success') => {
        setSnackbar({ open: true, message, severity })
    }

    const closeSnackbar = () => {
        setSnackbar((prev) => ({ ...prev, open: false }))
    }

    /* ------------------ Data Fetch ------------------ */

    const loadInvoices = useCallback(async () => {
        try {
            setLoading(true)

            const params = {
                skip: page * rowsPerPage,
                limit: rowsPerPage,
                ...filters,
            }

            const [invoiceResponse, billingResponse] = await Promise.all([
                fetchAllInvoices(params), // includes bank details now
                fetchBillingRecords({ paymentStatus: 'To Be Raised' }),
            ])

            let combined = []

            /* -------- Invoices -------- */
            if (invoiceResponse?.data) {
                const mappedInvoices = invoiceResponse.data.map(inv => ({
                    ...inv,
                    recordType: 'INVOICE',
                }))
                combined.push(...mappedInvoices)
                setTotal(invoiceResponse.total || 0)
            }

            /* -------- Billing Records -------- */
            const billingList = Array.isArray(billingResponse)
                ? billingResponse
                : billingResponse?.data || []

            const orgCache = {}

            const mappedBilling = await Promise.all(
                billingList.map(async (record) => {
                    let organizationName = record.organizationName || null

                    if (!organizationName && record.organizationId) {
                        const key = String(record.organizationId)
                        if (!orgCache[key]) {
                            try {
                                orgCache[key] = await fetchOrganizationName(record.organizationId)
                            } catch {
                                orgCache[key] = null
                            }
                        }
                        organizationName = orgCache[key]
                    }

                    return {
                        ...record,
                        _id: record.id || record._id,
                        recordType: 'BILLING',
                        invoiceNumber: 'Bill',
                        status: 'To Be Raised',
                        invoiceValue: record.amount || 0,
                        energyValue: record.actualGeneration || 0,
                        deemedEnergy: record.deemedGeneration || 0,
                        mainTariff: record.tariff || 0,
                        organizationName,
                    }
                })
            )

            setInvoices([...combined, ...mappedBilling])
        } catch (err) {
            console.error(err)
            showSnackbar('Failed to load invoice records', 'error')
        } finally {
            setLoading(false)
        }
    }, [page, rowsPerPage, filters])

    useEffect(() => {
        loadInvoices()
    }, [loadInvoices])

    /* ------------------ Filtering ------------------ */

    const filteredInvoices = invoices.filter(inv => {
        if (filters.plantName && inv.plantName !== filters.plantName) return false
        if (
            filters.organization &&
            (inv.organizationName || inv.organizationId) !== filters.organization
        ) return false
        if (
            filters.status &&
            (inv.status || inv.paymentStatus)?.toUpperCase() !== filters.status.toUpperCase()
        ) return false
        if (filters.period) {
            const invMonth = dayjs(inv.invoiceDate).format('MM-YYYY')
            const fltMonth = dayjs(filters.period).format('MM-YYYY')
            if (invMonth !== fltMonth) return false
        }
        return true
    })

    /* ------------------ Derived Data ------------------ */

    const plantNames = [...new Set(invoices.map(i => i.plantName).filter(Boolean))]
    const organizations = [...new Set(invoices.map(i => i.organizationName || i.organizationId).filter(Boolean))]
    const statusOptions = ['PAID', 'UNPAID', 'PENDING', 'OVERDUE', 'TO BE RAISED']

    /* ------------------ Selection ------------------ */

    const handleSelectInvoice = (id) => {
        setSelectedInvoices(prev =>
            prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
        )
    }

    const handleSelectAll = (checked) => {
        if (checked) {
            setSelectedInvoices(
                filteredInvoices
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map(inv => inv._id)
            )
        } else {
            setSelectedInvoices([])
        }
    }

    /* ------------------ Update Invoice ------------------ */

    const handleUpdateInvoice = async (id, updates) => {
        try {
            await updateInvoice(id, updates)

            const invoice = invoices.find(inv => inv._id === id)
            if (invoice?.recordType === 'INVOICE' && (updates.status || updates.paymentStatus)) {
                await syncBillingRecordFromInvoice(invoice.plantId, invoice.invoiceDate, {
                    paymentStatus: updates.status || updates.paymentStatus,
                    invoiceNumber: invoice.invoiceNumber,
                    invoiceDate: updates.invoiceDate || invoice.invoiceDate,
                    dueDate: updates.dueDate || invoice.dueDate,
                    invoiceValue: updates.invoiceValue || invoice.invoiceValue,
                })
            }

            showSnackbar('Invoice updated successfully')
            loadInvoices()
        } catch {
            showSnackbar('Failed to update invoice', 'error')
        }
    }

    /* ------------------ Create Invoice ------------------ */

    /*const handleCreateInvoice = async (data) => {
        try {
            const dateStr = dayjs(data.invoiceDate).format('YYYY-MM-DD')
            const last = data.organizationId
                ? await getLastInvoiceNumber(data.organizationId, dateStr)
                : {}

            const payload = {
                ...data,
                invoiceNumber:
                    last?.invoiceNumber ||
                    `INV-${dayjs().format('YYYYMM')}-${Math.floor(Math.random() * 9000) + 1000}`,
            }

            await createInvoice(payload)

            await updateBillingRecordWithInvoice(data.billingRecordId, {
                invoiceNumber: payload.invoiceNumber,
                paymentStatus: payload.paymentStatus,
            })

            showSnackbar('Invoice created successfully')
            loadInvoices()
        } catch {
            showSnackbar('Failed to create invoice', 'error')
        }
    }*/

    const handleCreateInvoice = async (data) => {
        try {
            setLoading(true)

            const dateStr = dayjs(data.invoiceDate).format('YYYY-MM-DD')
            let nextInv = { invoiceNumber: null }

            try {
                if (data.organizationId) {
                    nextInv = await getLastInvoiceNumber(data.organizationId, dateStr)
                }
            } catch (e) {
                console.warn('Failed to fetch last invoice number, fallback used', e)
            }

            const mainEnergy = parseFloat(data.energyValue || data.actualGeneration || 0)
            const mainTariff = parseFloat(data.mainTariff || data.tariff || 0)

            const deemedEnergy = parseFloat(
                data.deemedEnergy || data.deemedGeneration || 0
            )
            const deemedTariff = parseFloat(data.deemedTariff || 0)

            const mainAmount = mainEnergy * mainTariff
            const deemedAmount = deemedEnergy * deemedTariff
            const totalAmount = mainAmount + deemedAmount

            const subTotal = totalAmount
            const roundedTotal = Math.round(subTotal)
            const roundingOff = roundedTotal - subTotal

            const orgId =
                typeof data.organizationId === 'object' && data.organizationId?.$oid
                    ? data.organizationId.$oid
                    : data.organizationId

            const payload = {
                invoiceNumber:
                    nextInv.invoiceNumber ||
                    `INV-${dayjs().format('YYYYMM')}-${Math.floor(
                        Math.random() * 9000
                    ) + 1000}`,

                plantId: data.plantId,
                plantName: data.plantName,
                organizationId: orgId ? String(orgId) : null,

                invoiceValue: roundedTotal,
                roundingOff: parseFloat(roundingOff.toFixed(2)),

                energyValue: mainEnergy,
                mainTariff: mainTariff,

                deemedEnergy: deemedEnergy,
                deemedTariff: deemedTariff,

                deemedGeneration: parseFloat(deemedAmount.toFixed(2)),

                type: data.type || 'Regular',
                status: data.paymentStatus || 'UNPAID',
                paymentStatus: data.paymentStatus || 'UNPAID',

                invoiceDate: data.invoiceDate,
                dueDate: data.dueDate,

                automated: false,
                emailSent: false,

                remarks:
                    data.remarks || 'Invoice generated from billing record',
            }

            /* 5️⃣ Create invoice */
            const response = await createInvoice(payload)

            if (!response?.success) {
                showSnackbar(response?.message || 'Failed to generate invoice', 'error')
                return false
            }

            /* 6️⃣ Sync billing record*/
            try {
                await syncBillingRecordFromInvoice(payload.plantId, payload.invoiceDate, {
                    paymentStatus: payload.paymentStatus,
                    invoiceNumber: payload.invoiceNumber,
                    invoiceDate: payload.invoiceDate,
                    dueDate: payload.dueDate,
                    energyValue: payload.energyValue,
                    deemedEnergy: payload.deemedEnergy,
                    mainTariff: payload.mainTariff,
                    invoiceValue: payload.invoiceValue,
                })
            } catch (syncError) {
                console.warn(
                    'Billing sync failed, falling back to direct update',
                    syncError
                )

                await updateBillingRecordWithInvoice(data.billingRecordId, {
                    paymentStatus: payload.paymentStatus,
                    invoiceNumber: payload.invoiceNumber,
                    invoiceDate: payload.invoiceDate,
                    dueDate: payload.dueDate,
                    message: 'Direct update during invoice creation',
                })
            }

            showSnackbar('Invoice generated successfully', 'success')
            await loadInvoices()
            return true
        } catch (error) {
            console.error('Error generating invoice:', error)
            showSnackbar('Failed to generate invoice', 'error')
            return false
        } finally {
            setLoading(false)
        }
    }


    /* ------------------ Email ------------------ */

    const handleSendEmail = async (invoiceId, type, pdfBlob) => {
        try {
            const res = await sendInvoiceEmail(invoiceId, type, pdfBlob)
            showSnackbar(res.message, res.success ? 'success' : 'error')
            loadInvoices()
        } catch {
            showSnackbar('Failed to send email', 'error')
        }
    }

    /* ------------------ Zoho Export ------------------ */

    const handleExportToZoho = async (invoiceIds) => {
        setIsExporting(true)
        try {
            for (const id of invoiceIds) {
                const invoice = invoices.find(inv => inv._id === id)
                if (!invoice) continue
                const zohoState = await prepareZohoState(invoice)
                await exportToZoho({ invoice, zohoState })
            }
            showSnackbar('Exported to Zoho successfully')
            loadInvoices()
        } catch {
            showSnackbar('Zoho export failed', 'error')
        } finally {
            setIsExporting(false)
        }
    }

    /* ------------------ CSV Export ------------------ */

    const exportToCSV = () => {
        if (!filteredInvoices.length) return

        const headers = [
            'Plant Name',
            'Organization',
            'Invoice Number',
            'Invoice Date',
            'Due Date',
            'Amount',
            'Status',
        ]

        const rows = filteredInvoices.map(inv => [
            inv.plantName,
            inv.organizationName,
            inv.invoiceNumber,
            inv.invoiceDate,
            inv.dueDate,
            inv.invoiceValue,
            inv.status || inv.paymentStatus,
        ])

        const csv = [headers, ...rows].map(r => r.join(',')).join('\n')
        const blob = new Blob([csv], { type: 'text/csv' })
        const url = URL.createObjectURL(blob)

        const a = document.createElement('a')
        a.href = url
        a.download = `invoice_records_${dayjs().format('YYYYMMDD_HHmmss')}.csv`
        a.click()
        URL.revokeObjectURL(url)
    }

    /* ------------------ Pagination ------------------ */

    const handleChangePage = (_, newPage) => {
        setPage(newPage)
        setSelectedInvoices([])
    }

    const handleChangeRowsPerPage = (e) => {
        setRowsPerPage(parseInt(e.target.value, 10))
        setPage(0)
        setSelectedInvoices([])
    }

    /* ------------------ Public API ------------------ */

    return {
        invoices: filteredInvoices,
        loading,
        filters,
        setFilters,
        selectedInvoices,
        setSelectedInvoices,
        page,
        rowsPerPage,
        total,
        snackbar,
        plantNames,
        organizations,
        statusOptions,
        isExporting,

        handleSelectInvoice,
        handleSelectAll,
        handleUpdateInvoice,
        handleCreateInvoice,
        handleSendEmail,
        handleExportToZoho,
        exportToCSV,

        handleChangePage,
        handleChangeRowsPerPage,
        closeSnackbar,
        loadInvoices,
    }
}

export default useInvoiceRecords
